import { NgClass } from '@angular/common'
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core'
import { FormGroup, ReactiveFormsModule, Validators } from '@angular/forms'
import { InputErrorComponent, TooltipComponent } from '@components'
import { EluxButton, EluxIcon, EluxLoading, EluxModal } from '@elxjs/ui'

export type FileFormatType = 'JPEG' | 'PNG' | 'SVG' | 'PDF' | 'WEBP' | 'GIF'

export interface FileFormatInput {
  type: FileFormatType
  maxSizeKb: number
}

export interface FileFormatLimit {
  label: string
  mimeTypes: string[]
  extensions: string[]
  maxSizeKb: number
}

const FORMAT_DEFINITIONS: Record<FileFormatType, { mimeTypes: string[]; extensions: string[] }> = {
  JPEG: { mimeTypes: ['image/jpeg'], extensions: ['.jpg', '.jpeg'] },
  PNG: { mimeTypes: ['image/png'], extensions: ['.png'] },
  SVG: { mimeTypes: ['image/svg+xml'], extensions: ['.svg'] },
  PDF: { mimeTypes: ['application/pdf'], extensions: ['.pdf'] },
  WEBP: { mimeTypes: ['image/webp'], extensions: ['.webp'] },
  GIF: { mimeTypes: ['image/gif'], extensions: ['.gif'] },
}

let uniqueIdCounter = 0
function uniqueTooltipId(value: string | undefined): string {
  if (value && value.trim() !== '') return value
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `tooltip-UUID--${crypto.randomUUID()}`
  }
  // fallback for environments without crypto.randomUUID
  return `tooltip--${uniqueIdCounter++}`
}

@Component({
  selector: 'app-upload-file',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    EluxIcon,
    EluxModal,
    EluxButton,
    EluxLoading,
    InputErrorComponent,
    TooltipComponent,
    NgClass,
  ],
  templateUrl: './upload-file.component.html',
  styleUrls: ['./upload-file.component.scss'],
})
export class UploadFileComponent implements OnInit {
  @Input() formFile!: FormGroup | undefined
  @Input() formats: FileFormatInput[] = []
  @Input() ratio?: '' | '16:9' | '4:3' | '1:1' | '4:5' | '9:16' | '3:2' | '5:4' | '9:8' | '5:3' = ''
  @Input() width: number = 0
  @Input() height: number = 0
  @Input() operator: 'eq' | 'min' | 'max' = 'min'
  @Input() fileText: string = ''
  @Input() required: boolean = true
  @Input() altText: string = 'Texto alternativo'
  @Input() loading: boolean = false
  @Input() backgroundClass: 'bg-lightest-grey' | 'bg-white' = 'bg-lightest-grey'
  @Input() imageUploadTooltipId: string = uniqueTooltipId(undefined)
  @Output() onFileSelected = new EventEmitter<File | null>()

  resolvedFormats: FileFormatLimit[] = []
  text = ''

  previewUrl: string | ArrayBuffer | null = null
  problemFile: string[] = []
  isDragging: boolean = false
  showTooltip: boolean = false
  isPdf: boolean = false

  tooltipAltImage = `Textos alternativos (alt text) tornam imagens acessíveis para leitores de tela. Marque o checkbox se a imagem for apenas ilustrativa. Caso a imagem transmita informação extra, descreva-a conforme as normas de acessibilidade. <br> <a class="tooltip-link" target="_blank" href="https://www.w3.org/WAI/tutorials/images/">Ver normas.</a>`

  get fileId(): string {
    const name = this.formFile?.get('name')?.value || 'default'
    const cleanName = name.normalize('NFD').replace(/[\u0300-\u036f]|[^a-zA-Z0-9]/g, '')
    return `file_input--${cleanName}`
  }

  get formatLabels(): string {
    return this.resolvedFormats.map((f) => f.label).join(', ')
  }

  get dimensionLabel(): string {
    const op = this.operator === 'eq' ? '' : this.operator === 'min' ? 'mínima' : 'máxima'
    return `Dimensão ${op} da imagem: ${this.width}x${this.height} px`
  }

  formatSizeDisplay(kb: number): string {
    return kb >= 1024 ? `${kb / 1024} MB` : `${kb} KB`
  }

  getFileExtensionFromUrl(urlStr: string): string | null {
    try {
      const url = new URL(urlStr)
      const pathname = url.pathname
      const dotIndex = pathname.lastIndexOf('.')

      if (dotIndex !== -1 && dotIndex > pathname.lastIndexOf('/')) {
        return pathname.substring(dotIndex + 1).toLowerCase()
      }

      return null
    } catch (error) {
      console.log('[UploadFile] Invalid URL string provided', error)
      return null
    }
  }

  ngOnInit(): void {
    const fileUrl = this.formFile?.get('url')?.value

    if (fileUrl) {
      const extension = this.getFileExtensionFromUrl(fileUrl)
      this.isPdf = extension === 'pdf'
    } else {
      this.isPdf = false
    }

    this.resolvedFormats = this.formats.map((f) => ({
      label: f.type,
      maxSizeKb: f.maxSizeKb,
      ...FORMAT_DEFINITIONS[f.type],
    }))

    const formatParts = this.resolvedFormats.map((f) => `${f.label} (até ${this.formatSizeDisplay(f.maxSizeKb)})`)

    let joinedString = ''

    if (formatParts.length > 1) {
      const lastPart = formatParts.pop()
      joinedString = `${formatParts.join(', ')} e ${lastPart}`
    } else {
      joinedString = formatParts.join('')
    }

    const parts: string[] = [joinedString]

    if (this.ratio) parts.push(`Imagem na proporção ${this.ratio}`)

    if (this.width && this.height) {
      const opLabel =
        this.operator === 'eq'
          ? 'Dimensões sugeridas da imagem'
          : this.operator === 'min'
            ? 'Dimensões mínimas da imagem'
            : 'Dimensões máximas da imagem'

      parts.push(`${opLabel}: ${this.width}x${this.height} px`)
    }

    this.text = parts.join('<br />')

    const illustrativeControl = this.formFile?.get('illustrative')
    const illustrativeValue = illustrativeControl?.value
    const isIllustrativeDefault = illustrativeValue !== false

    if (illustrativeControl && illustrativeValue !== isIllustrativeDefault) {
      illustrativeControl.setValue(isIllustrativeDefault)
    }

    this.altChange(isIllustrativeDefault)
  }

  isPdfFile(file: File): boolean {
    const isPdfByMime = file.type === 'application/pdf'
    const isPdfByExtension = file.name.toLowerCase().endsWith('.pdf')
    return isPdfByMime || isPdfByExtension
  }

  onClickInput(fileInput: HTMLInputElement) {
    fileInput.click()
  }

  onFileChange(event: Event) {
    const input = event.target as HTMLInputElement
    const file = input.files?.[0]
    if (file) {
      this.validateAndEmit(file)
    }
  }

  onDragOver(event: DragEvent) {
    event.preventDefault()
    this.isDragging = true
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault()
    this.isDragging = false
  }

  onDrop(event: DragEvent) {
    event.preventDefault()
    this.isDragging = false
    const file = event.dataTransfer?.files?.[0]
    if (file) {
      this.validateAndEmit(file)
    }
  }

  private getAspectRatio = (width: number, height: number): string => {
    const getGCD = (a: number, b: number): number => {
      return b === 0 ? a : getGCD(b, a % b)
    }

    if (width <= 0 || height <= 0) {
      throw new Error('Width and height must be greater than zero.')
    }

    const gcd = getGCD(width, height)
    const ratioWidth = width / gcd
    const ratioHeight = height / gcd

    return `${ratioWidth}:${ratioHeight}`
  }

  private validateAndEmit(file: File) {
    const matchedFormat = this.resolvedFormats.find(
      (f) => f.mimeTypes.includes(file.type) || f.extensions.some((ext) => file.name.toLowerCase().endsWith(ext))
    )

    if (!matchedFormat) {
      this.problemFile.push('type')
      this.resetInputFile()
      return
    }

    if (file.size > matchedFormat.maxSizeKb * 1024) {
      this.problemFile.push('size')
      this.resetInputFile()
      return
    }

    const isPdfFile = this.isPdfFile(file)
    this.isPdf = isPdfFile

    if (isPdfFile) {
      this.formFile?.patchValue({ width: '0', height: '0' })
      this.altChange(this.formFile?.get('illustrative')?.value)
      this.onFileSelected.emit(file)
      return
    }

    const reader = new FileReader()

    reader.onerror = () => {
      this.problemFile.push('type')
      this.resetInputFile()
      console.log('[UploadFile] Error reading file', reader.error)
    }

    reader.onload = (e) => {
      const img = new Image()

      img.onerror = () => {
        this.problemFile.push('type')
        this.resetInputFile()
        console.log('[UploadFile] Error loading image', img.src)
      }

      img.onload = () => {
        const { width, height } = img

        if (this.operator === 'eq' && (width !== this.width || height !== this.height)) {
          this.problemFile.push('width_height')
          this.resetInputFile()
          return
        } else if (this.operator === 'min' && (width < this.width || height < this.height)) {
          this.problemFile.push('width_height')
          this.resetInputFile()
          return
        } else if (this.operator === 'max' && (width > this.width || height > this.height)) {
          this.problemFile.push('width_height')
          this.resetInputFile()
          return
        }

        // const aspectRatio = this.getAspectRatio(width, height)
        // const requiredRatio = this.ratio

        // if (!requiredRatio) {
        //   console.log('[UploadFile] No aspect ratio requirement specified, skipping.')
        // } else {
        //   if (requiredRatio !== aspectRatio) {
        //     this.problemFile.push('aspect_ratio')
        //     this.resetInputFile()
        //     return
        //   }
        // }

        this.formFile?.patchValue({ width: width + '', height: height + '' })
        this.altChange(this.formFile?.get('illustrative')?.value)
        this.onFileSelected.emit(file)
      }
      img.src = e.target?.result as string
    }
    reader.readAsDataURL(file)
  }

  resetInputFile() {
    const related = document.getElementById(this.fileId) as HTMLInputElement
    related.value = ''
  }

  async removeFile(event: Event) {
    event.preventDefault()
    this.resetInputFile()
    this.formFile?.reset()
    this.formFile?.patchValue({
      illustrative: true,
    })
    this.altChange(true)
    this.onFileSelected.emit(null)
  }

  onCheckboxChange(event: Event) {
    const checked = (event.target as HTMLInputElement).checked
    this.altChange(checked)
  }

  altChange(checked: boolean | null | undefined) {
    const altControl = this.formFile?.get('alt')
    if (!altControl) return
    const isIllustrative = checked !== false

    if (this.isPdf) {
      altControl.clearValidators()
      altControl.enable()
      altControl.updateValueAndValidity()
      return
    }

    if (isIllustrative) {
      altControl.clearValidators()
      altControl.disable()
      altControl.setValue('')
    } else {
      altControl.setValidators([Validators.required])
      altControl.enable()
    }

    altControl.updateValueAndValidity()
  }
}
